import { useState, useMemo, type FormEvent } from 'react';
import { X, Repeat } from 'lucide-react';
import { createUnavailability } from '../../hooks/useUnavailability';

interface Props {
  bandId: string;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function toDateStr(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function addWeeksToDate(dateStr: string, weeks: number): Date {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

function oneYearFrom(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setFullYear(d.getFullYear() + 1);
  return toDateStr(d);
}

function formatPreviewDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function AddUnavailabilityModal({ bandId, userId, onClose, onSuccess }: Props) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSeries, setIsSeries] = useState(false);
  const [everyWeeks, setEveryWeeks] = useState(2);
  const [seriesEndDate, setSeriesEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // When start date changes and series is enabled, auto-populate series end date to 1 year out
  function handleStartDateChange(value: string) {
    setStartDate(value);
    if (!endDate || endDate < value) setEndDate(value);
    if (isSeries && (!seriesEndDate || seriesEndDate <= value)) {
      setSeriesEndDate(oneYearFrom(value));
    }
  }

  function handleSeriesToggle(checked: boolean) {
    setIsSeries(checked);
    if (checked && startDate && !seriesEndDate) {
      setSeriesEndDate(oneYearFrom(startDate));
    }
  }

  // Calculate the duration (in days) between start and end for each occurrence
  const durationDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate + 'T00:00:00');
    const e = new Date(endDate + 'T00:00:00');
    return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  // Generate all recurring dates up to the series end date
  const seriesDates = useMemo(() => {
    if (!isSeries || !startDate || !endDate || !seriesEndDate || everyWeeks < 1) return [];
    const limit = new Date(seriesEndDate + 'T23:59:59');
    const dates: { start: string; end: string }[] = [];
    for (let i = 0; i < 200; i++) {
      const s = addWeeksToDate(startDate, i * everyWeeks);
      if (s > limit) break;
      const e = new Date(s);
      e.setDate(e.getDate() + durationDays);
      dates.push({ start: toDateStr(s), end: toDateStr(e) });
    }
    return dates;
  }, [isSeries, startDate, endDate, seriesEndDate, everyWeeks, durationDays]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (isSeries && seriesDates.length > 0) {
      const entries = seriesDates.map((d) => ({
        band_id: bandId,
        user_id: userId,
        start_date: d.start,
        end_date: d.end,
        reason: reason || undefined,
      }));

      const { error } = await createUnavailability(entries);
      if (error) {
        setError(error);
        setLoading(false);
      } else {
        onSuccess();
      }
    } else {
      const { error } = await createUnavailability({
        band_id: bandId,
        user_id: userId,
        start_date: startDate,
        end_date: endDate || startDate,
        reason: reason || undefined,
      });

      if (error) {
        setError(error);
        setLoading(false);
      } else {
        onSuccess();
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
          <h2 className="text-lg font-semibold">Add Unavailable Dates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <p className="text-sm text-gray-400">
            Mark dates when you can't make it (holidays, childcare, work commitments, etc.).
            Your bandmates will see these on the calendar.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">End Date *</label>
              <input
                type="date"
                required
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Holiday, Childcare, Work trip"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Recurring series toggle */}
          <div className="border border-gray-800 rounded-lg p-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isSeries}
                onChange={(e) => handleSeriesToggle(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0 cursor-pointer"
              />
              <Repeat className={`w-4 h-4 ${isSeries ? 'text-indigo-400' : 'text-gray-500'}`} />
              <span className={`text-sm font-medium ${isSeries ? 'text-gray-50' : 'text-gray-400'}`}>
                Recurring series
              </span>
            </label>

            {isSeries && (
              <div className="mt-3 space-y-3 pl-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Every</span>
                  <input
                    type="number"
                    min={1}
                    max={52}
                    value={everyWeeks}
                    onChange={(e) => setEveryWeeks(Math.max(1, Math.min(52, parseInt(e.target.value) || 1)))}
                    className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-50 text-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-400">week{everyWeeks !== 1 ? 's' : ''}</span>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Series ends</label>
                  <input
                    type="date"
                    value={seriesEndDate}
                    min={startDate}
                    onChange={(e) => setSeriesEndDate(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Defaults to 1 year from the start date</p>
                </div>

                {/* Preview of generated dates */}
                {startDate && seriesDates.length > 0 && (
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-2.5 max-h-[140px] overflow-y-auto">
                    <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">
                      Preview ({seriesDates.length} date{seriesDates.length !== 1 ? 's' : ''})
                    </div>
                    <div className="space-y-1">
                      {seriesDates.map((d, i) => (
                        <div key={i} className="text-xs text-gray-300 flex items-center gap-2">
                          <span className="text-gray-500 w-5 text-right">{i + 1}.</span>
                          <span>
                            {formatPreviewDate(d.start)}
                            {d.start !== d.end && <> – {formatPreviewDate(d.end)}</>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
            >
              {loading
                ? 'Adding...'
                : isSeries && seriesDates.length > 0
                  ? `Add ${seriesDates.length} Dates`
                  : 'Add Dates'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
